import React, { useState, useEffect, useRef } from 'react';
import api from '../api/axiosConfig';
import imageCompression from 'browser-image-compression';
import { Modal, Button, Form } from 'react-bootstrap';
import CustomDropdown from './CustomDropdown';
import { useModal } from '../contexts/ModalContext';



function IssueRegisterModal({ show, issueId, onClose, onSuccess }) {
    const [status, setStatus] = useState('REPORTED');
  // 이 화면에서 올린 사진을 하나로 묶는 값. 서버가 이걸로 사진을 이슈에
  // 귀속시키고, 등록하지 않고 나가면 청소 잡이 걷어간다.
  const sessionIdRef = useRef(
    (crypto.randomUUID && crypto.randomUUID()) ||
    `${Date.now()}-${Math.random().toString(36).slice(2)}`
  );
    const [title, setTitle] = useState('');

    // Issue Section State
    const [description, setDescription] = useState('');
    const [createdAt, setCreatedAt] = useState('');
    const [facilityId, setFacilityId] = useState('');
    const [sectionId, setSectionId] = useState('');
    const [issueFiles, setIssueFiles] = useState([]);
    const [existingIssuePhotoUrls, setExistingIssuePhotoUrls] = useState([]);

    // Resolution Section State
    const [resolutionDescription, setResolutionDescription] = useState('');
    const [resolvedAt, setResolvedAt] = useState('');
    const [resolutionFiles, setResolutionFiles] = useState([]);
    const [existingResolutionPhotoUrls, setExistingResolutionPhotoUrls] = useState([]);

    const [facilities, setFacilities] = useState([]);
    const [sections, setSections] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { showAlert } = useModal();

    // Refs
    const issueCameraInputRef = React.useRef(null);
    const issueAlbumInputRef = React.useRef(null);
    const resolutionCameraInputRef = React.useRef(null);
    const resolutionAlbumInputRef = React.useRef(null);

    const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return new Date(date.getTime() - (date.getTimezoneOffset() * 60000)).toISOString().slice(0, 16);
    };

    useEffect(() => {
        if (show && issueId) {
            // Edit mode
            api.get(`/issues/${issueId}`)
                .then(res => {
                    const issue = res.data;
                    setStatus(issue.status);
                    setTitle(issue.title);

                    // Issue Data
                    setDescription(issue.description);
                    setCreatedAt(formatDateForInput(issue.createdAt));
                    setFacilityId(issue.facilityId);
                    setSectionId(issue.sectionId || '');
                    setExistingIssuePhotoUrls(issue.photoUrls || []);

                    // Resolution Data
                    if (issue.status === 'RESOLVED') {
                        setResolutionDescription(issue.resolutionDescription || '');
                        setResolvedAt(formatDateForInput(issue.resolvedAt || new Date().toISOString()));
                        setExistingResolutionPhotoUrls(issue.resolutionPhotoUrls || []);
                    }
                })
                .catch(err => console.error('Error fetching issue details:', err));
        } else if (show) {
            // Create mode
            setStatus('REPORTED');
            setTitle('');
            setDescription('');
            setCreatedAt(formatDateForInput(new Date().toISOString()));
            setFacilityId('');
            setSectionId('');
            setIssueFiles([]);
            setExistingIssuePhotoUrls([]);

            setResolutionDescription('');
            setResolvedAt('');
            setResolutionFiles([]);
            setExistingResolutionPhotoUrls([]);
        }
    }, [show, issueId]);

    useEffect(() => {
        if (show) {
            // Fetch facilities when modal opens
            api.get('/facilities')
                .then(res => setFacilities(res.data))
                .catch(err => console.error(err));
        }
    }, [show]);

    useEffect(() => {
        if (facilityId) {
            // Fetch sections for selected facility
            api.get(`/facilities/${facilityId}`)
                .then(res => setSections(res.data.sections || []))
                .catch(err => console.error(err));
        } else {
            setSections([]);
        }
    }, [facilityId]);

    const handleIssueFileChange = (event) => {
        const newFiles = Array.from(event.target.files);
        const currentCount = existingIssuePhotoUrls.length + issueFiles.length;
        const totalPossible = 10;

        if (currentCount + newFiles.length > totalPossible) {
            const allowedCount = totalPossible - currentCount;
            if (allowedCount <= 0) {
                showAlert('알림', '사진은 최대 10장까지 등록 가능합니다.', 'warning');
                event.target.value = null;
                return;
            }
            showAlert('알림', `사진은 최대 10장까지 등록 가능합니다. 초과된 ${newFiles.length - allowedCount}장의 사진은 제외되었습니다.`, 'warning');
            setIssueFiles(prev => [...prev, ...newFiles.slice(0, allowedCount)]);
        } else {
            setIssueFiles(prev => [...prev, ...newFiles]);
        }
        event.target.value = null;
    };

    const handleResolutionFileChange = (event) => {
        const newFiles = Array.from(event.target.files);
        const currentCount = existingResolutionPhotoUrls.length + resolutionFiles.length;
        const totalPossible = 10;

        if (currentCount + newFiles.length > totalPossible) {
            const allowedCount = totalPossible - currentCount;
            if (allowedCount <= 0) {
                showAlert('알림', '사진은 최대 10장까지 등록 가능합니다.', 'warning');
                event.target.value = null;
                return;
            }
            showAlert('알림', `사진은 최대 10장까지 등록 가능합니다. 초과된 ${newFiles.length - allowedCount}장의 사진은 제외되었습니다.`, 'warning');
            setResolutionFiles(prev => [...prev, ...newFiles.slice(0, allowedCount)]);
        } else {
            setResolutionFiles(prev => [...prev, ...newFiles]);
        }
        event.target.value = null;
    };

    const uploadFiles = async (files) => {
        const uploadedUrls = [];
        for (const file of files) {
            const options = { maxSizeMB: 0.5, maxWidthOrHeight: 1920, useWebWorker: true };
            const compressedFile = await imageCompression(file, options);
            const formData = new FormData();
            formData.append('file', compressedFile, compressedFile.name);
                    formData.append('sessionId', sessionIdRef.current);
            const res = await api.post('/reports/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            uploadedUrls.push(res.data.url);
        }
        return uploadedUrls;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (isSubmitting) return;
        setIsSubmitting(true);

        try {
            // Upload Issue Files
            const newIssueUrls = await uploadFiles(issueFiles);
            const finalIssueUrls = [...existingIssuePhotoUrls, ...newIssueUrls];

            // Upload Resolution Files (if applicable)
            let finalResolutionUrls = [...existingResolutionPhotoUrls];
            if (status === 'RESOLVED') {
                const newResolutionUrls = await uploadFiles(resolutionFiles);
                finalResolutionUrls = [...finalResolutionUrls, ...newResolutionUrls];
            }

            const issueData = {
                title,
                description,
                facilityId: parseInt(facilityId),
                sectionId: sectionId ? parseInt(sectionId) : undefined,
                photoUrls: finalIssueUrls,
                createdAt: createdAt ? new Date(createdAt).toISOString() : undefined, // Include createdAt

                // Resolution Fields
                ...(status === 'RESOLVED' ? {
                    status: 'RESOLVED',
                    resolutionDescription,
                    resolutionPhotoUrls: finalResolutionUrls,
                    resolvedAt: resolvedAt ? new Date(resolvedAt).toISOString() : new Date().toISOString()
                } : {})
            };

            if (issueId) {
                await api.patch(`/issues/${issueId}`, issueData);
            } else {
                await api.post('/issues', { ...issueData, sessionId: sessionIdRef.current });
            }

            onSuccess();
        } catch (error) {
            console.error('Error saving issue:', error);
            showAlert('오류', '이슈 저장에 실패했습니다.', 'danger');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal show={show} onHide={onClose} size="lg" centered backdrop="static" dialogClassName="mx-3 mx-lg-auto">
            <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title className="fw-bold">{issueId ? '이슈 수정' : '이슈 등록'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form onSubmit={handleSubmit}>
                    {/* Resolution Section - Only if Resolved */}
                    {status === 'RESOLVED' && (
                        <div className="mb-5 pb-4 border-bottom">
                            <div className="d-flex align-items-center mb-3">
                                <i className="bi bi-check-square-fill text-primary fs-5 me-2"></i>
                                <h5 className="fw-bold m-0 text-primary">해결</h5>
                            </div>

                            <div className="row g-2 mb-3">
                                <div className="col-12">
                                    <Form.Control
                                        type="datetime-local"
                                        value={resolvedAt}
                                        onChange={e => setResolvedAt(e.target.value)}
                                        className="fw-bold"
                                    />
                                </div>
                            </div>

                            <Form.Control
                                as="textarea"
                                rows={5}
                                placeholder="조치 내용을 입력하세요"
                                value={resolutionDescription}
                                onChange={e => setResolutionDescription(e.target.value)}
                                className="mb-3"
                            />
                            <div className="text-end text-muted small mb-3">{resolutionDescription.length} / 500</div>

                            {/* Resolution Photos */}
                            <div className="mb-3">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <Form.Label className="fw-bold mb-0 text-primary">사진 {existingResolutionPhotoUrls.length + resolutionFiles.length}/10</Form.Label>
                                    <div className="btn-group">
                                        <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => resolutionCameraInputRef.current.click()}>
                                            <i className="bi bi-camera me-1"></i> 카메라
                                        </button>
                                        <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => resolutionAlbumInputRef.current.click()}>
                                            <i className="bi bi-images me-1"></i> 앨범
                                        </button>
                                    </div>
                                </div>
                                <input type="file" accept="image/*" capture="environment" ref={resolutionCameraInputRef} onChange={handleResolutionFileChange} className="d-none" />
                                <input type="file" accept="image/*" multiple ref={resolutionAlbumInputRef} onChange={handleResolutionFileChange} className="d-none" />

                                <div className="d-flex flex-wrap gap-2">
                                    {existingResolutionPhotoUrls.map((url, index) => (
                                        <div key={`res-existing-${index}`} className="position-relative">
                                            <img src={url} alt="existing" className="rounded border" style={{ width: '80px', height: '80px', objectFit: 'cover' }} />
                                            <button type="button" className="btn btn-dark btn-sm rounded-circle position-absolute top-0 end-0 p-0 d-flex justify-content-center align-items-center" style={{ width: '20px', height: '20px', transform: 'translate(50%, -50%)' }} onClick={() => setExistingResolutionPhotoUrls(prev => prev.filter((_, i) => i !== index))}><i className="bi bi-x"></i></button>
                                        </div>
                                    ))}
                                    {resolutionFiles.map((file, index) => (
                                        <div key={`res-new-${index}`} className="position-relative">
                                            <img src={URL.createObjectURL(file)} alt="preview" className="rounded border" style={{ width: '80px', height: '80px', objectFit: 'cover' }} />
                                            <button type="button" className="btn btn-dark btn-sm rounded-circle position-absolute top-0 end-0 p-0 d-flex justify-content-center align-items-center" style={{ width: '20px', height: '20px', transform: 'translate(50%, -50%)' }} onClick={() => setResolutionFiles(prev => prev.filter((_, i) => i !== index))}><i className="bi bi-x"></i></button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Issue Section */}
                    <div className="mb-3">
                        <div className="d-flex align-items-center mb-3">
                            <i className="bi bi-record-circle text-danger fs-5 me-2"></i>
                            <h5 className="fw-bold m-0 text-danger">이슈</h5>
                        </div>

                        <Form.Group className="mb-3">
                            <Form.Control
                                type="text"
                                placeholder="제목을 입력하세요 *"
                                value={title}
                                onChange={e => setTitle(e.target.value)}
                                required
                                className="fw-bold"
                            />
                        </Form.Group>

                        <div className="row g-2 mb-3">
                            <div className="col-12">
                                <Form.Control
                                    type="datetime-local"
                                    value={createdAt}
                                    onChange={e => setCreatedAt(e.target.value)}
                                />
                            </div>
                        </div>

                        <Form.Group className="mb-3">
                            <Form.Control
                                as="textarea"
                                rows={5}
                                placeholder="내용을 입력하세요"
                                value={description}
                                onChange={e => setDescription(e.target.value)}
                            />
                            <div className="text-end text-muted small">{description.length} / 500</div>
                        </Form.Group>

                        {/* Issue Photos */}
                        <div className="mb-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <Form.Label className="fw-bold mb-0 text-danger">사진 {existingIssuePhotoUrls.length + issueFiles.length}/10</Form.Label>
                                <div className="btn-group">
                                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => issueCameraInputRef.current.click()}>
                                        <i className="bi bi-camera me-1"></i> 카메라
                                    </button>
                                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => issueAlbumInputRef.current.click()}>
                                        <i className="bi bi-images me-1"></i> 앨범
                                    </button>
                                </div>
                            </div>
                            <input type="file" accept="image/*" capture="environment" ref={issueCameraInputRef} onChange={handleIssueFileChange} className="d-none" />
                            <input type="file" accept="image/*" multiple ref={issueAlbumInputRef} onChange={handleIssueFileChange} className="d-none" />

                            <div className="d-flex flex-wrap gap-2">
                                {existingIssuePhotoUrls.map((url, index) => (
                                    <div key={`issue-existing-${index}`} className="position-relative">
                                        <img src={url} alt="existing" className="rounded border" style={{ width: '80px', height: '80px', objectFit: 'cover' }} />
                                        <button type="button" className="btn btn-dark btn-sm rounded-circle position-absolute top-0 end-0 p-0 d-flex justify-content-center align-items-center" style={{ width: '20px', height: '20px', transform: 'translate(50%, -50%)' }} onClick={() => setExistingIssuePhotoUrls(prev => prev.filter((_, i) => i !== index))}><i className="bi bi-x"></i></button>
                                    </div>
                                ))}
                                {issueFiles.map((file, index) => (
                                    <div key={`issue-new-${index}`} className="position-relative">
                                        <img src={URL.createObjectURL(file)} alt="preview" className="rounded border" style={{ width: '80px', height: '80px', objectFit: 'cover' }} />
                                        <button type="button" className="btn btn-dark btn-sm rounded-circle position-absolute top-0 end-0 p-0 d-flex justify-content-center align-items-center" style={{ width: '20px', height: '20px', transform: 'translate(50%, -50%)' }} onClick={() => setIssueFiles(prev => prev.filter((_, i) => i !== index))}><i className="bi bi-x"></i></button>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="row mb-3">
                            <div className="col-md-6">
                                <Form.Label className="fw-bold text-secondary small">시설 *</Form.Label>
                                <CustomDropdown
                                    options={facilities.map(f => ({ value: f.id, label: f.name }))}
                                    value={parseInt(facilityId) || ''}
                                    onChange={e => setFacilityId(e.target.value)}
                                    placeholder="시설 선택"
                                />
                            </div>
                            <div className="col-md-6">
                                <Form.Label className="fw-bold text-secondary small">점검표 (구역)</Form.Label>
                                <CustomDropdown
                                    options={sections.map(s => ({ value: s.id, label: s.name }))}
                                    value={parseInt(sectionId) || ''}
                                    onChange={e => setSectionId(e.target.value)}
                                    placeholder={facilityId ? "구역 선택" : "시설을 먼저 선택하세요"}
                                    style={{ opacity: !facilityId ? 0.6 : 1, pointerEvents: !facilityId ? 'none' : 'auto' }}
                                />
                            </div>
                        </div>

                    </div>
                </Form>
            </Modal.Body>
            <Modal.Footer className="border-0 pt-0">
                <Button variant="light" onClick={onClose}>취소</Button>
                <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting} className="text-white">
                    {isSubmitting ? '저장 중...' : '저장'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default IssueRegisterModal;
